export interface RouteAlternativeTime {
    departureValue?: number; // unix seconds
    departureText?: string;
    arrivalValue?: number;
    arrivalText?: string;
}

export interface TransitRoute {
    summary: string;
    legs: {
        duration: { value: number; text: string };
        distance?: { value: number; text: string };
        steps: any[];
        start_address?: string;
        end_address?: string;
        start_location?: { lat: number; lng: number };
        end_location?: { lat: number; lng: number };
        [key: string]: any;
    }[];
    bounds?: any;
    copyrights?: string;
    overview_polyline?: { points: string };
    warnings?: string[];
    waypoint_order?: number[];
    fare?: any;
}

export interface TransitRouteResponse {
    routes: TransitRoute[];
    status: string;
}

const getTotalDuration = (route: TransitRoute): number => {
    return route.legs.reduce((total, leg) => total + (leg.duration?.value || 0), 0);
};

// Get the arrival time at destination for a route
// Uses arrival_time from API if available, otherwise calculates from current time + duration
const getArrivalTime = (route: TransitRoute): number => {
    const lastLeg = route.legs[route.legs.length - 1];

    // If the API provides arrival_time (common for transit routes), use it
    if (lastLeg?.arrival_time?.value) {
        return lastLeg.arrival_time.value;
    }

    // Otherwise, calculate arrival time as: now + total duration
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const totalDuration = getTotalDuration(route);
    return now + totalDuration;
};

// Map language names to Google API language codes
const languageCodeMap: Record<string, string> = {
    'french': 'fr',
    'franch': 'fr', // typo
    'français': 'fr',
    'english': 'en',
    'hebrew': 'he',
    'עברית': 'he',
    'portuguese': 'pt',
    'português': 'pt',
    'spanish': 'es',
    'español': 'es',
    'german': 'de',
    'deutsch': 'de',
    'italian': 'it',
    'italiano': 'it',
    'arabic': 'ar',
    'العربية': 'ar',
    'russian': 'ru',
    'русский': 'ru',
    'chinese': 'zh',
    '中文': 'zh',
    'japanese': 'ja',
    '日本語': 'ja',
};

const getLanguageCode = (language: string): string => {
    const lower = language.toLowerCase().trim();
    // If it's already a 2-letter code, return it
    if (lower.length === 2) return lower;
    // Otherwise, look up in map
    return languageCodeMap[lower] || 'en';
};

export type TravelMode = 'transit' | 'walking' | 'bicycling' | 'driving';

export const fetchTransitRoute = async (
    startLat: number,
    startLng: number,
    endLat: number,
    endLng: number,
    language: string,
    mode: TravelMode = 'transit'
): Promise<TransitRouteResponse | null> => {
    const langCode = getLanguageCode(language);
    console.log('Transit service - language input:', language, '→ code:', langCode);
    const url = `https://transitroutefunction-613221148836.europe-west1.run.app?startLat=${startLat}&startLng=${startLng}&endLat=${endLat}&endLng=${endLng}&language=${langCode}&mode=${mode}`;
    try {
        const response = await fetch(url);
        const data: any = await response.json().catch(() => null);

        // The cloud function returns { error: 'ZERO_RESULTS', message } when Google has no
        // route for the requested mode/area (most common: bicycling outside supported regions,
        // or walking/driving across non-routable terrain). Surface this as a typed error so
        // the consumer can show a mode-aware message instead of a generic failure toast.
        if (!response.ok || (data && (data.error || data.status && data.status !== 'OK'))) {
            const errStatus = data?.error || data?.status || `HTTP ${response.status}`;
            console.warn(`[TRANSIT] no routes — mode=${mode} status=${errStatus}`);
            const e = new Error(`NO_ROUTE_FOR_MODE:${mode}`);
            (e as any).mode = mode;
            (e as any).status = errStatus;
            throw e;
        }

        console.log('[TRANSIT] raw routes from API:', data.routes?.length, 'status:', data.status);

        // Group routes by their "shape" — sequence of transit lines + walking buckets.
        // Routes sharing a shape are the same path at different departure times; we collapse
        // them into one option and attach the other times as alternativeTimes.
        const getRouteSignature = (route: TransitRoute): string => {
            const parts: string[] = [];
            route.legs.forEach((leg) => {
                (leg.steps || []).forEach((step: any) => {
                    if (step.travel_mode === 'TRANSIT') {
                        const td = step.transit_details;
                        const line = td?.line?.short_name || td?.line?.name || td?.line?.vehicle?.type || '?';
                        parts.push(`T:${line}`);
                    } else if (step.travel_mode === 'WALKING') {
                        // Bucket walks at 50m so near-identical paths still match
                        const dist = Math.round((step.distance?.value || 0) / 50) * 50;
                        parts.push(`W:${dist}`);
                    } else {
                        parts.push(String(step.travel_mode));
                    }
                });
            });
            return parts.join('|');
        };

        const groups = new Map<string, TransitRoute[]>();
        for (const route of data.routes) {
            const sig = getRouteSignature(route);
            console.log('[TRANSIT] route signature:', sig, '| summary:', route.summary, '| legs:', route.legs.length);
            const list = groups.get(sig);
            if (list) list.push(route);
            else groups.set(sig, [route]);
        }
        console.log('[TRANSIT] grouped into', groups.size, 'unique shapes:',
            Array.from(groups.entries()).map(([sig, g]) => ({ sig, count: g.length })));

        // Within each group, sort by arrival time. Pick earliest as representative,
        // and merge the alternatives' transit-step times into the representative's steps
        // so each "Take bus 507 ..." step lists every available departure for that segment.
        const collapsed: TransitRoute[] = [];
        for (const group of groups.values()) {
            group.sort((a, b) => getArrivalTime(a) - getArrivalTime(b));
            const main = group[0];
            if (group.length === 1) {
                console.log('[TRANSIT] solo group, no merge needed:', main.summary);
                collapsed.push(main);
                continue;
            }
            console.log('[TRANSIT] merging group of', group.length, 'routes for shape:', main.summary);
            try {
                // Shallow clone the structure we mutate; leave deep step objects alone
                // except for the steps we actually annotate (cloned per-step below).
                const merged: TransitRoute = {
                    ...main,
                    legs: main.legs.map((leg) => ({
                        ...leg,
                        steps: (leg.steps || []).map((step: any) => {
                            if (step.travel_mode !== 'TRANSIT') return step;
                            return { ...step };
                        }),
                    })),
                };

                merged.legs.forEach((leg, legIdx) => {
                    (leg.steps || []).forEach((step: any, stepIdx: number) => {
                        if (step.travel_mode !== 'TRANSIT') return;
                        // Google rounds the text to the minute but the value is precise to the second,
                        // so two departures one second apart show the same text. Dedupe by both.
                        const seenKeys = new Set<string>();
                        const keyFor = (v: number | undefined, text: string | undefined) =>
                            `${typeof v === 'number' ? Math.floor(v / 60) : 'x'}|${text ?? ''}`;
                        const mainTd = step.transit_details;
                        if (mainTd?.departure_time) {
                            seenKeys.add(keyFor(mainTd.departure_time.value, mainTd.departure_time.text));
                        }
                        const alts: RouteAlternativeTime[] = [];
                        for (let i = 1; i < group.length; i++) {
                            const altStep: any = group[i].legs?.[legIdx]?.steps?.[stepIdx];
                            const td = altStep?.transit_details;
                            if (altStep?.travel_mode === 'TRANSIT' && td?.departure_time) {
                                const v = td.departure_time.value as number | undefined;
                                const key = keyFor(v, td.departure_time.text);
                                if (seenKeys.has(key)) continue;
                                seenKeys.add(key);
                                alts.push({
                                    departureValue: v,
                                    departureText: td.departure_time.text,
                                    arrivalValue: td.arrival_time?.value,
                                    arrivalText: td.arrival_time?.text,
                                });
                            }
                        }
                        if (alts.length > 0) {
                            step.alternativeDepartures = alts;
                            console.log('[TRANSIT]   step', legIdx + ':' + stepIdx, 'attached', alts.length, 'alt departures:',
                                alts.map(a => a.departureText).join(', '));
                        }
                    });
                });
                console.log('[TRANSIT] merged route OK:', merged.summary);
                collapsed.push(merged);
            } catch (err) {
                console.error('[TRANSIT] MERGE FAILED for shape', main.summary, '— falling back. Error:', err);
                collapsed.push(main);
            }
        }
        console.log('[TRANSIT] returning', collapsed.length, 'collapsed routes');

        // Sort all unique routes by earliest arrival; return all (no slice).
        const sortedRoutes = collapsed.sort((a, b) => getArrivalTime(a) - getArrivalTime(b));

        return {
            ...data,
            routes: sortedRoutes
        };
    } catch (error: any) {
        console.error('Error fetching transit route:', error);
        // Re-throw typed errors so consumers can show mode-aware messages instead of a generic toast.
        if (typeof error?.message === 'string' && error.message.startsWith('NO_ROUTE_FOR_MODE:')) {
            throw error;
        }
        return null;
    }
};
